/**
 * The avatar as a module, for layouts where it is not the scene behind everything (the Essenziale
 * theme). Which face shows is decided once, for every place (avatar/FaceView.tsx: Settings, then what is
 * available, then the theme's stand-in — Essenziale's is the orb); the module only frames it:
 *  - MuseTalk     the face cropped in a circle (standard) or a rounded portrait, with a ring that shows
 *                 what the voice does; the idle avatar the same way;
 *  - three.js     its page in the same frame;
 *  - particles    the orb on its own, no frame: it shows the state and the cues itself.
 *
 * The face crop is an estimate on the 720×1280 MuseTalk frame (FACE below): tune it on the totem. The idle avatar's
 * own is in the stage catalog (Avatar.face).
 */
import type { CSSProperties, ReactNode } from 'react';
import { IdleFace, LiveFace, ParticlesFace, useFaceKind, useFaceState } from '../../avatar/FaceView';
import { useStage } from '../../react/hooks';
import { AVATAR_BOX } from '../../shell/scenes';
import css from './Avatar.module.css';

/** Where the face is in the MuseTalk frame (fractions), and how tall the frame is per circle diameter. */
export const FACE = { cx: 0.505, cy: 0.36, frameHeightPerDiameter: 2.9 } as const;

const FRAME_ASPECT = 9 / 16;

/** The frame placed so that the face (`face`, fractions of the frame) is in the middle of the circle. */
function faceFrameStyle(face: { cx: number; cy: number; frameHeightPerDiameter: number } = FACE): CSSProperties {
  const h = face.frameHeightPerDiameter * 100;
  const w = h * FRAME_ASPECT;
  return { height: `${h}%`, width: `${w}%`, left: `${50 - face.cx * w}%`, top: `${50 - face.cy * h}%` };
}

function FramedFace({ shape }: { shape: 'orb' | 'portrait' }) {
  const { kind, standIn } = useFaceKind('musetalk');
  const state = useFaceState();
  const { avatar } = useStage();
  const orb = <div className={css[shape === 'orb' ? 'orbFree' : 'portraitFree']}><ParticlesFace /></div>;
  const framed = (face: ReactNode, crop?: Parameters<typeof faceFrameStyle>[0]) => (
    <div className={`${css[shape]} ${css[state] ?? ''}`} data-state={state}>
      {kind === 'lowpoly' ? face : <div className={css.faceFrame} style={faceFrameStyle(crop)}>{face}</div>}
    </div>
  );
  const idle = () => framed(<IdleFace />, avatar?.face);
  if (kind === 'particles') return <div className={css.slot}>{orb}</div>;
  if (kind === 'idle') return <div className={css.slot}>{idle()}</div>;
  // until the outside face streams, the theme's stand-in shows (Essenziale: the orb)
  const standInFace = standIn === 'idle' ? idle() : orb;
  return <div className={css.slot}><LiveFace kind={kind} box={AVATAR_BOX} standIn={<div className={css.slot}>{standInFace}</div>} frame={framed} /></div>;
}

/** Standard: a small circle. */
export function AvatarOrb() {
  return <FramedFace shape="orb" />;
}

/** A larger rounded frame. */
export function AvatarPortrait() {
  return <FramedFace shape="portrait" />;
}
